import { useState } from "react";
import { useNotify, useRefresh, type Identifier } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";

import type { ReassignLeadResult } from "../carouselTypes";
import { ReassignDialog, type ReassignSubmit } from "./ReassignDialog";

/**
 * Almikia Epic EL3: a button that opens the reassignment dialog (advisor + mandatory reason) and
 * runs `reassign` (a provider method). The server enforces who may reassign; its Spanish error
 * text is shown inline. `invalidateKey` refreshes the surrounding screen's query on success.
 */
export const ReassignActionButton = ({
  label,
  reassign,
  invalidateKey,
}: {
  label: string;
  reassign: (params: {
    newSalesId: Identifier;
    reason: string;
  }) => Promise<ReassignLeadResult>;
  invalidateKey?: string;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const onSubmit: ReassignSubmit = async ({
    newSalesId,
    advisorName,
    reason,
  }) => {
    setSaving(true);
    try {
      await reassign({ newSalesId, reason });
      notify("crm.reassign.success", {
        type: "success",
        messageArgs: { name: advisorName },
      });
      if (invalidateKey) {
        await queryClient.invalidateQueries({ queryKey: [invalidateKey] });
      }
      refresh();
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : "";
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="h-11 sm:h-9"
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <ReassignDialog
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={onSubmit}
        saving={saving}
      />
    </>
  );
};
